import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';

import Checkbox from '@zvuk-b2b/react-uikit/ui/Checkbox';
import IconComment from '@zvuk-b2b/react-uikit/ui/icons/Comment';

import { DATA_TEST_IDS, MAX_VISIBLE_ADDRESS_TAGS } from '../../consts';
import { TEXTS } from '../../texts';
import type { ReportAddressOptionProps } from './types';

import styles from './index.module.scss';

const ReportAddressOption: FC<ReportAddressOptionProps> = ({ address, isChecked, onChange }) => {
  const [isTagsExpanded, setIsTagsExpanded] = useState(false);

  const visibleTags = useMemo(
    () => (isTagsExpanded ? address.tags : address.tags.slice(0, MAX_VISIBLE_ADDRESS_TAGS)),
    [address.tags, isTagsExpanded]
  );
  const hiddenTagsCount = Math.max(address.tags.length - MAX_VISIBLE_ADDRESS_TAGS, 0);
  const isTagsToggleVisible = hiddenTagsCount > 0;

  const handleChange = useCallback(
    (value: boolean) => {
      onChange(address.id, value);
    },
    [address.id, onChange]
  );

  const handleTagsToggle = useCallback(() => {
    setIsTagsExpanded((currentValue) => !currentValue);
  }, []);

  return (
    <li
      className={styles.item}
      data-testid={DATA_TEST_IDS.AUDIO_REPORT_ADDRESS_OPTION(address.id)}>
      <Checkbox
        elementId={DATA_TEST_IDS.AUDIO_REPORT_ADDRESS_OPTION_CHECKBOX(address.id)}
        buttonClassName={styles.checkboxButton}
        isChecked={isChecked}
        onChange={handleChange}
      />

      <div className={styles.content}>
        <div className={styles.header}>
          <span
            className={styles.address}
            data-testid={DATA_TEST_IDS.AUDIO_REPORT_ADDRESS_OPTION_TEXT(address.id)}>
            {address.address}
          </span>
          {address.comment && (
            <span
              className={styles.comment}
              data-testid={DATA_TEST_IDS.AUDIO_REPORT_ADDRESS_OPTION_COMMENT(address.id)}>
              <IconComment className={styles.commentIcon} />
              <span
                className={styles.commentText}
                data-testid={DATA_TEST_IDS.AUDIO_REPORT_ADDRESS_OPTION_COMMENT_TEXT(address.id)}>
                {address.comment}
              </span>
            </span>
          )}
        </div>

        {address.tags.length > 0 && (
          <div className={styles.tags}>
            {visibleTags.map((tag) => (
              <span
                key={tag.id}
                className={styles.tag}
                data-testid={DATA_TEST_IDS.AUDIO_REPORT_ADDRESS_OPTION_TAG(address.id, tag.id)}>
                {tag.title}
              </span>
            ))}

            {isTagsToggleVisible && (
              <button
                type="button"
                className={classNames(styles.tag, styles.moreTag)}
                data-testid={
                  isTagsExpanded
                    ? DATA_TEST_IDS.AUDIO_REPORT_ADDRESS_OPTION_HIDE_TAGS_BUTTON(address.id)
                    : DATA_TEST_IDS.AUDIO_REPORT_ADDRESS_OPTION_MORE_TAGS_BUTTON(address.id)
                }
                onClick={handleTagsToggle}>
                {isTagsExpanded
                  ? TEXTS.AUDIO_REPORT_ADDRESSES_HIDE_TAGS
                  : TEXTS.AUDIO_REPORT_ADDRESSES_MORE_TAGS(hiddenTagsCount)}
              </button>
            )}
          </div>
        )}
      </div>
    </li>
  );
};

export default memo(ReportAddressOption);
